import type {
  LoanDataExportCandidate,
  LoanDataExportCandidateFilters,
  LoanDataExportCandidateSearchInput,
  LoanDataExportDateField
} from '@/types/loan-data-export';

export const LOAN_DATA_EXPORT_DATE_FIELDS: Array<{
  value: LoanDataExportDateField;
  label: string;
}> = [
  { value: 'funded', label: 'Funded date' },
  { value: 'closing', label: 'Closing date' },
  { value: 'application', label: 'Application date' },
  { value: 'created', label: 'Created date' },
  { value: 'last_modified', label: 'Last modified date' }
];

export function createDefaultLoanDataExportFilters(): LoanDataExportCandidateFilters {
  return {
    search: '',
    productCategories: [],
    investors: [],
    dateField: 'funded',
    dateFrom: '',
    dateTo: '',
    folders: [],
    purposeCategories: [],
    purposes: [],
    programs: [],
    channelCategories: [],
    loanOfficers: [],
    statuses: [],
    states: [],
    nonQmDocTypes: [],
    lienPositions: [],
    rateTypes: [],
    ausDecisions: [],
    minAmount: '',
    maxAmount: ''
  };
}

function selected(values: string[]): string[] | undefined {
  return values.length > 0 ? values : undefined;
}

function amount(value: string): number | undefined {
  if (value.trim() === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

export function buildLoanDataExportCandidateSearchInput(
  filters: LoanDataExportCandidateFilters,
  page: number,
  pageSize: number
): LoanDataExportCandidateSearchInput {
  return {
    search: filters.search.trim() || undefined,
    productCategories: selected(filters.productCategories),
    investors: selected(filters.investors),
    dateField: filters.dateField,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
    folders: selected(filters.folders),
    purposeCategories: selected(filters.purposeCategories),
    purposes: selected(filters.purposes),
    programs: selected(filters.programs),
    channelCategories: selected(filters.channelCategories),
    loanOfficers: selected(filters.loanOfficers),
    statuses: selected(filters.statuses),
    states: selected(filters.states),
    nonQmDocTypes: selected(filters.nonQmDocTypes),
    lienPositions: selected(filters.lienPositions),
    rateTypes: selected(filters.rateTypes),
    ausDecisions: selected(filters.ausDecisions),
    minAmount: amount(filters.minAmount),
    maxAmount: amount(filters.maxAmount),
    page,
    pageSize
  };
}

export function countLoanDataExportFilterGroups(filters: LoanDataExportCandidateFilters): number {
  return [
    filters.productCategories.length > 0,
    filters.investors.length > 0,
    Boolean(filters.dateFrom || filters.dateTo),
    filters.folders.length > 0,
    filters.purposeCategories.length > 0,
    filters.purposes.length > 0,
    filters.programs.length > 0,
    filters.channelCategories.length > 0,
    filters.loanOfficers.length > 0,
    filters.statuses.length > 0,
    filters.states.length > 0,
    filters.nonQmDocTypes.length > 0,
    filters.lienPositions.length > 0,
    filters.rateTypes.length > 0,
    filters.ausDecisions.length > 0,
    Boolean(filters.minAmount || filters.maxAmount)
  ].filter(Boolean).length;
}

export function countAdvancedLoanDataExportFilterGroups(filters: LoanDataExportCandidateFilters): number {
  return [
    filters.folders.length > 0,
    filters.purposeCategories.length > 0,
    filters.purposes.length > 0,
    filters.programs.length > 0,
    filters.channelCategories.length > 0,
    filters.loanOfficers.length > 0,
    filters.statuses.length > 0,
    filters.states.length > 0,
    filters.nonQmDocTypes.length > 0,
    filters.lienPositions.length > 0,
    filters.rateTypes.length > 0,
    filters.ausDecisions.length > 0,
    Boolean(filters.minAmount || filters.maxAmount)
  ].filter(Boolean).length;
}

export function loanDataExportDateFieldLabel(field: LoanDataExportDateField): string {
  return LOAN_DATA_EXPORT_DATE_FIELDS.find((option) => option.value === field)?.label ?? 'Date';
}

export function getLoanDataExportCandidateDate(
  candidate: LoanDataExportCandidate,
  field: LoanDataExportDateField
): string | null {
  if (field === 'funded') {
    return candidate.fundedDate ?? null;
  }
  if (field === 'closing') {
    return candidate.closingDate ?? null;
  }
  if (field === 'application') {
    return candidate.applicationDate ?? null;
  }
  if (field === 'created') {
    return candidate.createdDate ?? null;
  }
  return candidate.lastModifiedDate ?? null;
}
